import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  CircleHelp,
  ChevronLeft,
  ChevronRight,
  History,
  ListChecks,
  Loader2,
  Maximize2,
  MessageSquareText,
  Minimize2,
  Pencil,
  RefreshCw,
  Rocket,
  ShieldCheck,
  Sparkles,
  Target,
} from "lucide-react";
import { ApiError, core } from "../../lib/api";
import { useToast } from "../../components/ui/toast";
import { cn } from "../../lib/utils";
import { PromptMarkdown } from "./prompt-markdown";

/** The exact client-editable contract exposed by techladder-core. */
const SECTION_ORDER = ["first_message", "objective", "guidelines", "flow", "faqs"] as const;
type SectionName = typeof SECTION_ORDER[number];

const SECTION_META = {
  first_message: {
    label: "First message",
    help: "The exact line the agent speaks when the call connects.",
    icon: MessageSquareText,
    rows: 3,
  },
  objective: {
    label: "Objective",
    help: "Who the agent represents and what the conversation should achieve.",
    icon: Target,
    rows: 4,
  },
  guidelines: {
    label: "Conversation guidelines",
    help: "Tone, wording, and business rules the agent should follow.",
    icon: ShieldCheck,
    rows: 7,
  },
  flow: {
    label: "Call flow",
    help: "The steps of the conversation, in order.",
    icon: ListChecks,
    rows: 9,
  },
  faqs: {
    label: "Frequently asked questions",
    help: "Common caller questions and the answers the agent should give.",
    icon: CircleHelp,
    rows: 7,
  },
} as const;

interface SectionValue {
  text: string;
  items: unknown[];
}

interface VersionRow {
  version_id: string;
  version_name: string;
  published_at?: string;
  is_current: boolean;
}

interface ExternalPromptResponse {
  agent_id?: string;
  status?: string;
  error?: string;
  source_prompt_hash?: string;
  enabled: boolean;
  supported: boolean;
  has_internal_prompt: boolean;
  sections: Record<string, SectionValue>;
  draft: { sections: Record<string, SectionValue>; saved_at?: string } | null;
  stale: boolean;
  template_updated_at?: string;
  version_id?: string;
  versions: VersionRow[];
}

function textOf(sections: Record<string, SectionValue> | undefined, name: string): string {
  return sections?.[name]?.text ?? "";
}

export function ExternalPromptEditor({
  agentId,
  sessionId,
  error,
  data,
  loading,
  reload,
  canAdminister,
  onDirtyChange,
  pollGaveUp,
  expanded = false,
  onExpandedChange,
}: {
  agentId: string;
  sessionId?: string;
  error?: string;
  data: ExternalPromptResponse | null;
  loading: boolean;
  reload: () => void;
  canAdminister: boolean;
  onDirtyChange?: (dirty: boolean) => void;
  // True once the parent stops auto-polling a long-pending template (~90s).
  // The panel swaps the spinner for a leave-and-return message; a late
  // `ready` is still picked up by manual refresh or revisit.
  pollGaveUp?: boolean;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}) {
  const toast = useToast();
  const navigate = useNavigate();
  const endpoint = sessionId
    ? `/api/v1/prompt-builder/sessions/${encodeURIComponent(sessionId)}/external-prompt`
    : `/api/v1/agents/${encodeURIComponent(agentId)}/external-prompt`;
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [publishing, setPublishing] = useState(false);
  const [resplitting, setResplitting] = useState(false);
  const [restoring, setRestoring] = useState("");
  const [problems, setProblems] = useState<string[]>([]);
  const [showVersions, setShowVersions] = useState(false);
  const [activeSection, setActiveSection] = useState<SectionName>("first_message");
  const [editing, setEditing] = useState(false);

  const sections = data?.sections;
  const unpublished = data?.draft?.sections;

  // Refill the form only when the template revision actually changed.
  // Every fetch returns a new object identity, so gating on [sections]
  // alone would wipe in-progress typing on any reload (resplit, restore,
  // manual retry). Refs — not state in the dep array — let the effect
  // *read* dirtiness without *subscribing* to keystrokes (which would
  // re-fire the effect per keystroke and make the form untypable).
  const revision = `${data?.source_prompt_hash ?? ""}:${data?.version_id ?? ""}`;
  const lastRevision = useRef<string | undefined>(undefined);
  const lastHadDraft = useRef(false);
  const dirtyRef = useRef(false);

  useEffect(() => {
    if (!sections) return;
    const hasDraft = unpublished != null;
    if (lastRevision.current === revision && lastHadDraft.current === hasDraft && dirtyRef.current) {
      return; // Same revision, form is dirty: keep typing.
    }
    lastRevision.current = revision;
    lastHadDraft.current = hasDraft;
    const source = unpublished ?? sections;
    setDrafts(Object.fromEntries(SECTION_ORDER.map((name) => [name, textOf(source, name)])));
    setProblems([]);
    setEditing(false);
  }, [revision, sections, unpublished]);

  const dirty = useMemo(() => {
    if (!sections) return false;
    return SECTION_ORDER.some((name) => (drafts[name] ?? "") !== textOf(sections, name));
  }, [drafts, sections]);
  dirtyRef.current = dirty;

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  const handlePublish = useCallback(async () => {
    if (publishing) return;
    setPublishing(true);
    setProblems([]);
    try {
      const result = await core<{ agent_id?: string }>(`${endpoint}/publish`, {
        method: "POST",
        body: JSON.stringify({
          ...Object.fromEntries(
            SECTION_ORDER.map((name) => [name, { text: drafts[name] ?? "", items: [] }]),
          ),
          source_prompt_hash: data?.source_prompt_hash,
          source_version_id: data?.version_id,
        }),
      });
      toast.success(
        sessionId ? "Agent created" : "Changes published",
        sessionId
          ? "Your agent is ready to review and test."
          : "The live agent now uses the updated business prompt.",
      );
      if (sessionId && result.agent_id) {
        navigate(`/home/create-agent?agent_id=${encodeURIComponent(result.agent_id)}`);
      } else {
        reload();
      }
    } catch (caught) {
      const apiError = caught as ApiError;
      const found = apiError?.data?.problems || apiError?.data?.detail?.problems;
      if (Array.isArray(found) && found.length) {
        setProblems(found.map(String));
      } else if (apiError?.status === 409) {
        // The prompt drifted since load; the server already saved the typed
        // text as a draft, so the way forward is a reload, not a retry.
        toast.danger(
          "Prompt changed while editing",
          "Your text is saved on the server. Reload to get the current version, then publish again.",
        );
        reload();
      } else if (apiError?.status === 502) {
        // Regeneration failed; the draft is likewise saved server-side.
        toast.danger("Couldn't publish right now", "Your text is saved. Retry shortly.");
      } else {
        toast.danger("Could not publish", apiError.message);
      }
    } finally {
      setPublishing(false);
    }
  }, [data?.source_prompt_hash, data?.version_id, drafts, endpoint, navigate, publishing, reload, sessionId, toast]);

  async function handleResplit() {
    if (resplitting || !agentId) return;
    setResplitting(true);
    try {
      await core(`/api/v1/agents/${encodeURIComponent(agentId)}/external-prompt/resplit`, {
        method: "POST",
      });
      toast.success("Prompt preparation started", "The sections will refresh when they are ready.");
      reload();
    } catch (caught) {
      toast.danger("Could not prepare the prompt", (caught as Error).message);
    } finally {
      setResplitting(false);
    }
  }

  async function handleRestore(version: VersionRow) {
    if (restoring) return;
    setRestoring(version.version_id);
    try {
      await core(
        `/api/v1/agents/${encodeURIComponent(agentId)}/versions/${encodeURIComponent(version.version_id)}/restore`,
        { method: "POST", body: JSON.stringify({}) },
      );
      toast.success(`Restored ${version.version_name}`, "The agent is running that version again.");
      reload();
    } catch (caught) {
      toast.danger("Could not restore that version", (caught as Error).message);
    } finally {
      setRestoring("");
    }
  }

  if (loading && !data) {
    return (
      <section className="min-h-[520px] rounded-xl border border-line bg-elevated p-5 shadow-sm" aria-live="polite">
        <div className="flex items-center gap-3 border-b border-line pb-5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-soft text-brand">
            <Loader2 className="h-4 w-4 animate-spin" />
          </span>
          <div>
            <p className="text-sm font-bold text-ink">Preparing your business prompt</p>
            <p className="mt-0.5 text-xs text-ink-3">Loading the editable sections for this agent.</p>
          </div>
        </div>
        <div className="mt-5 space-y-3">
          {[0, 1, 2, 3].map((item) => (
            <div key={item} className="animate-pulse rounded-lg border border-line bg-soft p-4">
              <div className="h-3 w-32 rounded bg-line" />
              <div className="mt-3 h-16 rounded bg-line/70" />
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="min-h-[320px] rounded-xl border border-danger/30 bg-elevated p-6 shadow-sm">
        <div className="flex items-start gap-3 py-8">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-danger" />
          <div>
            <p className="text-sm font-bold text-ink">The business prompt could not be loaded</p>
            <p className="mt-1 text-xs leading-5 text-ink-3">{error}</p>
            <button type="button" onClick={reload} className="mt-4 inline-flex items-center gap-2 rounded-md bg-ink px-3 py-2 text-xs font-bold text-canvas">
              <RefreshCw className="h-3.5 w-3.5" /> Retry
            </button>
          </div>
        </div>
      </section>
    );
  }

  if (data?.supported === false) {
    return (
      <section className="rounded-xl border border-line bg-elevated p-6 text-sm shadow-sm">
        Business prompt editing is not available for this agent architecture.
      </section>
    );
  }

  if (data?.status === "pending") {
    return (
      <section className="min-h-[420px] rounded-xl border border-line bg-elevated p-6 shadow-sm" aria-live="polite">
        <div className="flex items-start justify-between gap-4 py-8">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-soft text-brand">
              <Loader2 className="h-4 w-4 animate-spin" />
            </span>
            <div>
              <p className="text-sm font-bold text-ink">
                {pollGaveUp ? "Still working on your sections" : "Building your editable sections"}
              </p>
              <p className="mt-1 max-w-lg text-xs leading-5 text-ink-3">
                {pollGaveUp
                  ? "This is taking longer than usual. You can leave this page — preparation continues and the sections will be ready when you return."
                  : "We are organizing the live agent into business-friendly sections. This page refreshes automatically when they are ready."}
              </p>
            </div>
          </div>
          <button type="button" onClick={reload} className="inline-flex h-9 shrink-0 items-center gap-2 rounded-md border border-line px-3 text-xs font-bold text-ink hover:border-brand">
            <RefreshCw className="h-3.5 w-3.5" /> Check now
          </button>
        </div>
      </section>
    );
  }

  if (data?.status === "failed") {
    return (
      <section className="min-h-[360px] rounded-xl border border-danger/30 bg-elevated p-6 shadow-sm">
        <div className="flex items-start gap-3 py-8">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-danger-soft text-danger">
            <AlertTriangle className="h-4 w-4" />
          </span>
          <div className="flex-1">
            <p className="text-sm font-bold text-ink">The prompt could not be prepared</p>
            <p className="mt-1 max-w-lg text-xs leading-5 text-ink-3">
              {data.error || "The operation could not finish. Please retry."}
            </p>
            <button type="button" onClick={reload} className="mt-4 inline-flex items-center gap-2 rounded-md bg-ink px-3 py-2 text-xs font-bold text-canvas">
              <RefreshCw className="h-3.5 w-3.5" /> Retry preparation
            </button>
          </div>
        </div>
      </section>
    );
  }

  if (data && !data.has_internal_prompt) {
    return (
      <section className="min-h-[360px] rounded-xl border border-line bg-elevated p-6 shadow-sm">
        <div className="flex items-start justify-between gap-4 py-8">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-soft text-brand">
              <Sparkles className="h-4 w-4" />
            </span>
            <div>
              <p className="text-sm font-bold text-ink">This agent is still being prepared</p>
              <p className="mt-1 max-w-lg text-xs leading-5 text-ink-3">
                Once the first production prompt is available, its business sections will appear here automatically.
              </p>
            </div>
          </div>
          {canAdminister && agentId && (
            <button type="button" onClick={handleResplit} disabled={resplitting} className="inline-flex h-9 shrink-0 items-center gap-2 rounded-md border border-line px-3 text-xs font-bold text-ink hover:border-brand disabled:opacity-40">
              {resplitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              Prepare prompt
            </button>
          )}
        </div>
      </section>
    );
  }

  if (!data) return null;

  const activeIndex = SECTION_ORDER.indexOf(activeSection);
  const activeMeta = SECTION_META[activeSection];
  const ActiveIcon = activeMeta.icon;

  return (
    <div className="space-y-3">
      {data.stale && (
        <section className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
            <div className="flex-1">
              <p className="text-xs font-bold text-ink">The live prompt changed after these sections were prepared</p>
              <p className="mt-1 text-xs leading-5 text-ink-3">Publishing is paused to protect the newer live version.</p>
              <button type="button" onClick={canAdminister ? handleResplit : reload} disabled={resplitting} className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-line bg-elevated px-2.5 py-1.5 text-[11px] font-bold text-ink hover:border-brand disabled:opacity-40">
                {resplitting ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                {canAdminister ? "Rebuild from the live prompt" : "Reload sections"}
              </button>
            </div>
          </div>
        </section>
      )}

      {unpublished && (
        <section className="rounded-lg border border-warning/30 bg-warning-soft p-4">
          <p className="text-xs font-bold text-ink">Recovered unpublished changes</p>
          <p className="mt-1 text-xs leading-5 text-ink-2">The last publish did not complete, so your saved draft has been restored below.</p>
        </section>
      )}

      {problems.length > 0 && (
        <section className="rounded-lg border border-danger/30 bg-danger-soft p-4">
          <p className="text-xs font-bold text-ink">This prompt cannot be published yet</p>
          <ul className="mt-2 list-disc space-y-1 pl-4">
            {problems.map((problem) => <li key={problem} className="text-xs text-ink-2">{problem}</li>)}
          </ul>
        </section>
      )}

      <section className="rounded-xl border border-line bg-elevated shadow-sm">
        <div className="flex flex-col gap-4 border-b border-line p-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-brand-soft px-2 py-1 font-mono text-[9px] font-bold uppercase tracking-wide text-brand">Business prompt</span>
              {dirty && <span className="text-[10px] font-bold text-warning">Unpublished changes</span>}
            </div>
            <h2 className="mt-2 text-base font-bold text-ink">Shape how your agent handles conversations</h2>
            <p className="mt-1 max-w-2xl text-xs leading-5 text-ink-3">Update the customer-facing content below. Technical instructions and integrations remain protected.</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {!sessionId && (
              <button type="button" onClick={() => setShowVersions((open) => !open)} aria-expanded={showVersions} className="inline-flex h-9 items-center gap-1.5 rounded-md border border-line bg-elevated px-3 text-xs font-bold text-ink hover:border-brand">
                <History className="h-3.5 w-3.5" /> Versions
              </button>
            )}
            <button type="button" onClick={handlePublish} disabled={publishing || (!dirty && !sessionId) || data.stale} className="inline-flex h-9 items-center gap-1.5 rounded-md bg-brand px-4 text-xs font-bold text-white disabled:cursor-not-allowed disabled:opacity-40">
              {publishing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Rocket className="h-3.5 w-3.5" />}
              {publishing ? (sessionId ? "Creating…" : "Publishing…") : (sessionId ? "Create agent" : "Publish changes")}
            </button>
          </div>
        </div>

        {showVersions && (
          <div className="mx-5 mt-5 divide-y divide-line rounded-lg border border-line">
            {data.versions.length === 0 && <p className="px-3 py-3 text-xs text-ink-3">No published versions yet.</p>}
            {data.versions.map((version) => (
              <div key={version.version_id} className="flex items-center justify-between gap-3 px-3 py-2.5">
                <div className="min-w-0">
                  <p className="truncate text-xs font-bold text-ink">
                    {version.version_name}
                    {version.is_current && <span className="ml-2 text-[10px] text-success">LIVE</span>}
                  </p>
                  {version.published_at && <p className="text-[11px] text-ink-3">{String(version.published_at).slice(0, 19).replace("T", " ")}</p>}
                </div>
                {!version.is_current && (
                  <button type="button" onClick={() => handleRestore(version)} disabled={Boolean(restoring)} className="shrink-0 rounded-md border border-line px-2 py-1 text-[11px] font-bold text-ink hover:border-brand disabled:opacity-40">
                    {restoring === version.version_id ? "Restoring…" : "Restore"}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="grid min-h-[520px] md:grid-cols-[230px_minmax(0,1fr)]">
          <nav aria-label="Prompt sections" className="border-b border-line bg-soft/45 p-3 md:border-b-0 md:border-r">
            <div className="mb-3 hidden px-2 md:block">
              <p className="font-mono text-[9px] font-bold uppercase tracking-wider text-ink-3">Prompt sections</p>
              <p className="mt-1 text-[11px] leading-4 text-ink-3">Choose one section to edit without losing your place.</p>
            </div>
            <div className="grid grid-cols-2 gap-1 sm:grid-cols-5 md:grid-cols-1">
              {SECTION_ORDER.map((name, index) => {
                const meta = SECTION_META[name];
                const Icon = meta.icon;
                const selected = activeSection === name;
                const length = (drafts[name] ?? "").length;
                return (
                  <button
                    key={name}
                    type="button"
                    aria-label={`Edit ${meta.label}`}
                    aria-current={selected ? "step" : undefined}
                    onClick={() => {
                      setActiveSection(name);
                      setEditing(false);
                    }}
                    className={cn(
                      "group flex min-w-0 items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left transition-colors md:min-h-[58px]",
                      selected
                        ? "border-brand/30 bg-elevated text-ink shadow-sm"
                        : "border-transparent text-ink-2 hover:border-line hover:bg-elevated/70 hover:text-ink",
                    )}
                  >
                    <span className={cn(
                      "flex h-7 w-7 shrink-0 items-center justify-center rounded-md",
                      selected ? "bg-brand-soft text-brand" : "bg-elevated text-ink-3 group-hover:text-ink-2",
                    )}>
                      <Icon className="h-3.5 w-3.5" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[11px] font-bold">{index + 1}. {meta.label}</span>
                      <span className="mt-0.5 hidden font-mono text-[9px] text-ink-3 md:block">{length.toLocaleString()} chars</span>
                    </span>
                    <ChevronRight className={cn("hidden h-3.5 w-3.5 shrink-0 md:block", selected ? "text-brand" : "text-ink-3 opacity-0 group-hover:opacity-100")} />
                  </button>
                );
              })}
            </div>
          </nav>

          <div className="flex min-w-0 flex-col p-4 sm:p-5">
            <div className="flex items-start gap-3 border-b border-line pb-4">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-line bg-soft text-brand">
                <ActiveIcon className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-bold text-ink">
                    {activeIndex + 1}. {activeMeta.label}
                  </p>
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    <span className="rounded-full bg-soft px-2 py-1 font-mono text-[9px] text-ink-3">
                      {(drafts[activeSection] ?? "").length.toLocaleString()} chars
                    </span>
                    <button
                      type="button"
                      onClick={() => setEditing((current) => !current)}
                      className="inline-flex h-8 items-center gap-1.5 rounded-md border border-line bg-elevated px-2.5 text-[11px] font-bold text-ink hover:border-brand"
                    >
                      {editing ? <Sparkles className="h-3.5 w-3.5" /> : <Pencil className="h-3.5 w-3.5" />}
                      {editing ? "Preview" : "Edit markdown"}
                    </button>
                    {onExpandedChange && (
                      <button
                        type="button"
                        onClick={() => onExpandedChange(!expanded)}
                        aria-pressed={expanded}
                        className="inline-flex h-8 items-center gap-1.5 rounded-md border border-line bg-elevated px-2.5 text-[11px] font-bold text-ink hover:border-brand"
                      >
                        {expanded ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
                        {expanded ? "Show test panel" : "Focus prompt"}
                      </button>
                    )}
                  </div>
                </div>
                <p className="mt-1 text-[11px] leading-5 text-ink-3">{activeMeta.help}</p>
              </div>
            </div>

            {editing ? (
              <textarea
                id={`external-${activeSection}`}
                aria-label={`${activeIndex + 1}. ${activeMeta.label}`}
                rows={20}
                value={drafts[activeSection] ?? ""}
                onChange={(event) => setDrafts((current) => ({ ...current, [activeSection]: event.target.value }))}
                className={cn(
                  "mt-4 w-full flex-1 resize-y rounded-lg border border-line bg-elevated px-5 py-4 font-mono text-[13px] leading-6 text-ink outline-none",
                  expanded ? "min-h-[calc(100vh-270px)]" : "min-h-[460px]",
                  "placeholder:text-ink-3 focus:border-brand focus:ring-2 focus:ring-brand/10",
                )}
              />
            ) : (
              <PromptMarkdown
                id={`external-${activeSection}`}
                ariaLabel={`${activeMeta.label} markdown preview`}
                content={drafts[activeSection] ?? ""}
                emptyMessage="Nothing has been added to this section yet. Use Edit markdown to add content."
                className={cn(
                  "mt-4 flex-1",
                  expanded ? "min-h-[calc(100vh-270px)]" : "min-h-[460px] max-h-[68vh]",
                )}
              />
            )}

            <div className="mt-4 flex items-center justify-between border-t border-line pt-4">
              <button
                type="button"
                disabled={activeIndex === 0}
                onClick={() => setActiveSection(SECTION_ORDER[activeIndex - 1])}
                className="inline-flex h-8 items-center gap-1.5 rounded-md border border-line px-2.5 text-[11px] font-bold text-ink hover:border-brand disabled:cursor-not-allowed disabled:opacity-30"
              >
                <ChevronLeft className="h-3.5 w-3.5" /> Previous
              </button>
              <span className="font-mono text-[9px] font-bold uppercase tracking-wide text-ink-3">Section {activeIndex + 1} of {SECTION_ORDER.length}</span>
              <button
                type="button"
                disabled={activeIndex === SECTION_ORDER.length - 1}
                onClick={() => setActiveSection(SECTION_ORDER[activeIndex + 1])}
                className="inline-flex h-8 items-center gap-1.5 rounded-md border border-line px-2.5 text-[11px] font-bold text-ink hover:border-brand disabled:cursor-not-allowed disabled:opacity-30"
              >
                Next <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export type { ExternalPromptResponse };
